import { SimpleSearchFilter } from 'module/api/filter/types';
import {
  defaultGroupedFilter,
  GroupedFilter,
} from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { isFieldFilterValid } from 'module/types/filter-field/grouped-filter/utils';
import {
  CustomLayoutDrawerContent,
  DrawerBreadcrumb,
  DrawerBreadCrumbs,
} from 'module/ui/components/atoms/drawer';
import { Loading } from 'module/ui/components/atoms/loading';
import { getApiGroupedFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-grouped-filter-transformer';
import { Button } from '@rocketlane/ui';
import { useGetAllCompanyPageTemplatesQuery } from 'queries/company-page-template';
import { useGetAllEmailPageTemplatesQuery } from 'queries/email-page-templare';
import { useGetAllMeetingPageTemplatesQuery } from 'queries/meeting-page-template';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AddAiPagesFilterSection } from './filter-section';
import { PageTemplatesGrid } from './grid';
import { useAiPagesFields } from './use-field';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-06);
  padding-top: var(--spacing-03);
  height: 100%;
`;
const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

export type AiPagesPayload = {
  filter?: SimpleSearchFilter;
  selectedTemplates: number[];
};

interface Props {
  onClose: () => void;
  onNext: (payload: AiPagesPayload) => Promise<void>;
  breadcrumbs?: DrawerBreadcrumb[];
}
export const AddAiPages: FC<Props> = ({ onClose, onNext, breadcrumbs }) => {
  const [filter, setFilter] = useState<GroupedFilter>(
    defaultGroupedFilter(FilterGroupType.Company),
  );
  const { fields, lookups } = useAiPagesFields();
  const {
    data: meetingPageTemplates,
    isLoading: isMeetingPageTemplatesLoading,
  } = useGetAllMeetingPageTemplatesQuery();
  const { data: emailPageTemplates, isLoading: isEmailPageTemplatesLoading } =
    useGetAllEmailPageTemplatesQuery();
  const {
    data: companyPageTemplates,
    isLoading: isCompanyPageTemplatesLoading,
  } = useGetAllCompanyPageTemplatesQuery();
  const allTemplates = useMemo(() => {
    return [
      ...(meetingPageTemplates ?? []),
      ...(emailPageTemplates ?? []),
      ...(companyPageTemplates ?? []),
    ];
  }, [meetingPageTemplates, emailPageTemplates, companyPageTemplates]);
  const isLoading =
    isMeetingPageTemplatesLoading ||
    isEmailPageTemplatesLoading ||
    isCompanyPageTemplatesLoading;
  const [selectedTemplates, setSelectedTemplates] = useState<number[]>([]);
  const [actionLoading, setActionLoading] = useState(false);

  const handleNext = useCallback(() => {
    setActionLoading(true);
    const isValidFilter = filter.groupCriteria.some(({ criteria }) =>
      isFieldFilterValid(criteria),
    );
    const filterTransformer = getApiGroupedFilterTransformer(
      lookups,
      FilterGroupType.Company,
    );

    onNext({
      filter: isValidFilter ? filterTransformer.to(filter) : undefined,
      selectedTemplates,
    }).finally(() => {
      setActionLoading(false);
    });
  }, [filter, lookups, selectedTemplates, onNext]);

  return (
    <CustomLayoutDrawerContent>
      <CustomLayoutDrawerContent.Title
        breadcrumbs={
          breadcrumbs ? (
            <DrawerBreadCrumbs breadcrumbs={breadcrumbs} />
          ) : undefined
        }
      >
        <FormattedMessage id="knowledge_base.ai_pages.title" />
      </CustomLayoutDrawerContent.Title>
      <CustomLayoutDrawerContent.Content>
        <Wrapper>
          <AddAiPagesFilterSection
            filter={filter}
            onChange={setFilter}
            fields={fields}
          />
          {isLoading ? (
            <LoadingWrapper>
              <Loading />
            </LoadingWrapper>
          ) : (
            <PageTemplatesGrid
              data={allTemplates ?? []}
              onSelectionChange={setSelectedTemplates}
            />
          )}
        </Wrapper>
      </CustomLayoutDrawerContent.Content>
      <CustomLayoutDrawerContent.Footer $rightAlignedAction>
        <Button variant="secondary" onClick={onClose}>
          <FormattedMessage id="cancel" />
        </Button>
        <Button
          variant="primary"
          onClick={handleNext}
          disabled={selectedTemplates.length === 0}
          loading={actionLoading}
        >
          <FormattedMessage id="knowledge_base.drawer.actions.add_to_knowledge_base" />
        </Button>
      </CustomLayoutDrawerContent.Footer>
    </CustomLayoutDrawerContent>
  );
};
